import { useState } from "react";
import { Link } from "react-router-dom";
import Nav from "../components/site/Nav";
import { ArrowUpRight, Copy, Check, Download, Flame, Quotes, ShareNetwork } from "@phosphor-icons/react";

import PressAdminGate from "./press/PressAdminGate";
import InstagramAssets from "./press/InstagramAssets";
import VideoScripts from "./press/VideoScripts";
import { HASHTAGS, IMAGES, QUICK_FACTS, tributeKitZipUrl } from "./press/pressData";

export default function Press() {
  // Admin-only page — token is stored by /admin login.
  const [adminToken, setAdminToken] = useState(() => {
    try { return window.localStorage.getItem("sws_admin_token") || ""; } catch { return ""; }
  });
  const [copiedAll, setCopiedAll] = useState(false);
  const [shared, setShared] = useState(false);

  const downloadZip = async () => {
    try {
      // Prefer the new full tribute kit (admin-only, private) if built
      const r = await fetch(tributeKitZipUrl(adminToken));
      if (r.ok) {
        const blob = await r.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "sweat-for-sacrifice-social-kit.zip";
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        return;
      }
      // Fallback to admin press zip (captions / photos / launch kit)
      const r2 = await fetch("/api/press/kit.zip", { headers: { "X-Admin-Token": adminToken } });
      if (!r2.ok) throw new Error(`HTTP ${r2.status}`);
      const blob = await r2.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "sweat-for-sacrifice-press-kit.zip";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Press: download failed:", err);
    }
  };

  const copyHashtags = async () => {
    try {
      await navigator.clipboard.writeText(HASHTAGS.join(" "));
      setCopiedAll(true);
      setTimeout(() => setCopiedAll(false), 2000);
    } catch (err) { console.warn("Press: hashtag copy failed:", err); }
  };

  const sharePage = async () => {
    const url = typeof window !== "undefined" ? window.location.origin + "/press" : "https://sweatwithslay.com/press";
    const shareData = {
      title: "Sweat for Sacrifice · Ambassador Kit",
      text: "911 minutes of movement in September. Every name lives on the Legacy Wall — forever. Here's everything you need to share the campaign.",
      url,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(url);
        setShared(true);
        setTimeout(() => setShared(false), 2500);
      }
    } catch (err) {
      if (err && err.name !== "AbortError") console.warn("Press: sharePage failed:", err);
    }
  };

  return (
    <main className="min-h-screen bg-[#0A0A0A] text-white">
      <Nav />

      {!adminToken && <PressAdminGate onUnlock={setAdminToken} />}

      {adminToken && (
      <>
      {/* Hero */}
      <section className="px-4 md:px-8 py-16 md:py-24 max-w-6xl mx-auto">
        <p className="text-[#B32027] uppercase tracking-[0.5em] text-xs sm:text-sm font-bold mb-6">/ Ambassador &amp; press kit</p>
        <h1 className="font-display font-black uppercase tracking-tighter leading-[0.85] text-5xl md:text-7xl lg:text-8xl mb-4">
          Sweat for<br /><span className="text-[#B32027]">Sacrifice.</span>
        </h1>
        <p className="font-brush text-4xl md:text-6xl text-white/85 -rotate-2 inline-block mb-8">who are you moving for?</p>
        <p className="text-white/80 text-lg md:text-xl leading-relaxed max-w-3xl mb-8">
          Everything you need to share, cover, or amplify the inaugural Sweat for Sacrifice campaign. Copy the post, save the photo, tag it up — no permission needed.
        </p>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={downloadZip}
            data-testid="press-download-zip"
            className="inline-flex items-center gap-2 bg-[#B32027] hover:bg-[#D42730] text-white px-6 py-4 text-[11px] font-bold uppercase tracking-[0.3em] shadow-[0_0_30px_rgba(179,32,39,0.4)] transition-colors"
          >
            <Download size={14} weight="bold" /> Download full kit (.zip)
          </button>
          <button
            onClick={sharePage}
            data-testid="press-share-page"
            className={`inline-flex items-center gap-2 px-6 py-4 text-[11px] font-bold uppercase tracking-[0.3em] transition-colors ${
              shared
                ? "bg-white text-[#B32027]"
                : "border-2 border-[#B32027] text-[#B32027] hover:bg-[#B32027] hover:text-white"
            }`}
          >
            {shared ? <><Check size={14} weight="bold" /> Link copied</> : <><ShareNetwork size={14} weight="fill" /> Share this page</>}
          </button>
          <Link
            to="/sweat-for-sacrifice"
            data-testid="press-cta-campaign"
            className="inline-flex items-center gap-2 border-2 border-white/20 hover:border-[#B32027] hover:text-[#B32027] text-white/85 px-6 py-4 text-[11px] font-bold uppercase tracking-[0.3em] transition-colors"
          >
            <Flame size={14} weight="fill" /> The campaign page
          </Link>
          <a
            href="mailto:Admin@sweatwithslay.com?subject=Sweat%20for%20Sacrifice%20-%20press%20inquiry"
            data-testid="press-cta-contact"
            className="inline-flex items-center gap-2 border-2 border-white/20 hover:border-[#B32027] hover:text-[#B32027] text-white/85 px-6 py-4 text-[11px] font-bold uppercase tracking-[0.3em] transition-colors"
          >
            Press inquiry
          </a>
        </div>
        <p className="text-white/50 text-xs mt-3 uppercase tracking-[0.2em] font-bold">Zip contains photos, captions, hashtags, video scripts + launch kit — ~1.5 MB.</p>
      </section>

      {/* Quick facts */}
      <section className="px-4 md:px-8 py-12 md:py-16 max-w-6xl mx-auto border-t border-white/10">
        <p className="text-[#B32027] uppercase tracking-[0.4em] text-xs font-bold mb-6">/ The facts</p>
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
          {QUICK_FACTS.map((f) => (
            <div key={f.label} className="border border-white/15 bg-[#0F0F0F] p-4">
              <p className="text-white/55 uppercase tracking-[0.3em] text-[10px] font-bold mb-2">{f.label}</p>
              <p className="text-white font-display font-black uppercase tracking-tighter text-base md:text-lg leading-tight">{f.value}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Founder story */}
      <section className="px-4 md:px-8 py-12 md:py-16 max-w-6xl mx-auto border-t border-white/10">
        <div className="grid md:grid-cols-2 gap-10 items-start">
          <div>
            <p className="text-[#B32027] uppercase tracking-[0.4em] text-xs font-bold mb-4">/ The story</p>
            <h2 className="font-display font-black uppercase tracking-tighter leading-[0.9] text-3xl md:text-5xl mb-6">Two uncles.<br /><span className="text-[#B32027]">One legacy.</span></h2>
            <div className="space-y-4 text-white/85 text-base md:text-lg leading-relaxed">
              <p>Sweat for Sacrifice was created by <span className="text-white font-semibold">AJ Johnson</span>, founder of Sweat With Slay, in honor of her Uncle Timmy Goss (FDNY Marine Co. 1, passed June 2026) and Uncle Harry D&rsquo;Onofrio (passed 2021).</p>
              <p>Since 2021, AJ has quietly climbed 110 floors on a stairmaster every September to honor her Uncle Harry and the fallen of 9/11. When Uncle Timmy passed in June 2026 — while AJ was launching Sweat With Slay after leaving her master-trainer role — she turned the private ritual into a public campaign anyone can join.</p>
              <p className="font-serif italic text-lg md:text-xl text-white">The point is not perfection. The point is purpose.</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {IMAGES.slice(0, 4).map((img) => (
              <a
                key={img.src}
                href={img.src}
                target="_blank"
                rel="noreferrer noopener"
                data-testid={`press-image-${img.src.split("/").pop().replace(".webp","")}`}
                className="group block border border-white/15 hover:border-[#B32027] bg-[#0F0F0F] overflow-hidden transition-colors"
              >
                <div className="aspect-square bg-black overflow-hidden">
                  <img src={img.src} alt={img.label} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <p className="text-white/75 text-[10px] uppercase tracking-[0.2em] font-bold p-3 group-hover:text-[#B32027] flex items-center gap-2">{img.label} <Download size={11} weight="bold" className="ml-auto" /></p>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Photo library */}
      <section className="px-4 md:px-8 py-12 md:py-16 max-w-6xl mx-auto border-t border-white/10">
        <div className="flex items-end justify-between gap-4 mb-6 flex-wrap">
          <div>
            <p className="text-[#B32027] uppercase tracking-[0.4em] text-xs font-bold mb-2">/ Photo library</p>
            <h2 className="font-display font-black uppercase tracking-tighter leading-[0.9] text-3xl md:text-4xl">Right-click to save.</h2>
          </div>
          <p className="text-white/55 text-xs uppercase tracking-[0.2em] font-bold">Please credit @sweatwithslay when sharing.</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {IMAGES.map((img) => (
            <a
              key={img.src}
              href={img.src}
              target="_blank"
              rel="noreferrer noopener"
              className="group block border border-white/15 hover:border-[#B32027] bg-[#0F0F0F] overflow-hidden transition-colors"
            >
              <div className="aspect-square bg-black overflow-hidden">
                <img src={img.src} alt={img.label} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <p className="text-white/75 text-[10px] uppercase tracking-[0.2em] font-bold p-3 group-hover:text-[#B32027] flex items-center gap-2">{img.label} <Download size={11} weight="bold" className="ml-auto" /></p>
            </a>
          ))}
        </div>
      </section>

      <InstagramAssets />

      {/* Hashtag bank */}
      <section className="px-4 md:px-8 py-12 md:py-16 max-w-6xl mx-auto border-t border-white/10">
        <div className="flex items-end justify-between gap-4 mb-4 flex-wrap">
          <div>
            <p className="text-[#B32027] uppercase tracking-[0.4em] text-xs font-bold mb-2">/ Hashtag bank</p>
            <h2 className="font-display font-black uppercase tracking-tighter leading-[0.9] text-3xl md:text-4xl">Grab &rsquo;em all.</h2>
          </div>
          <button
            onClick={copyHashtags}
            data-testid="press-copy-hashtags"
            className={`inline-flex items-center gap-2 px-5 py-3 text-[10px] font-bold uppercase tracking-[0.25em] transition-all ${
              copiedAll ? "bg-[#B32027] text-white" : "border-2 border-[#B32027] text-[#B32027] hover:bg-[#B32027] hover:text-white"
            }`}
          >
            {copiedAll ? <><Check size={12} weight="bold" /> Copied</> : <><Copy size={12} weight="bold" /> Copy all</>}
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {HASHTAGS.map((h) => (
            <span key={h} className="border border-white/15 bg-[#0F0F0F] text-white/85 px-3 py-2 text-sm">{h}</span>
          ))}
        </div>
      </section>

      <VideoScripts downloadZip={downloadZip} />

      {/* Contact */}
      <section className="px-4 md:px-8 py-16 md:py-24 max-w-4xl mx-auto text-center border-t border-white/10">
        <Quotes size={32} weight="fill" className="text-[#B32027] mx-auto mb-6" />
        <h2 className="font-display font-black uppercase tracking-tighter leading-[0.9] text-3xl md:text-5xl mb-4">
          Sharing this?<br /><span className="text-[#B32027]">Thank you.</span>
        </h2>
        <p className="text-white/75 text-base md:text-lg leading-relaxed max-w-2xl mx-auto mb-8">
          Tag <span className="text-white font-semibold">@sweatwithslay</span> so we can amplify. Press inquiries, partnership questions, or media requests — go straight to AJ.
        </p>
        <a
          href="mailto:Admin@sweatwithslay.com?subject=Sweat%20for%20Sacrifice%20-%20press%20inquiry"
          className="inline-flex items-center gap-2 bg-[#B32027] hover:bg-[#D42730] text-white px-8 py-4 text-[11px] font-bold uppercase tracking-[0.3em] shadow-[0_0_40px_rgba(179,32,39,0.5)] transition-colors"
        >
          Admin@sweatwithslay.com <ArrowUpRight size={12} weight="bold" />
        </a>
      </section>
      </>
      )}
    </main>
  );
}
